import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, Heart } from 'lucide-react';
import { footerNavigation, socialLinks } from '../../data/navigation';
import { COMPANY_INFO } from '../../utils/constants';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const iconMap = {
    Facebook,
    Instagram,
    Twitter,
    Youtube
  };

  return (
    <footer className="bg-gradient-to-br from-pragyavan-brown to-pragyavan-earth text-pragyavan-cream relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pragyavan-gold via-accent-gold to-pragyavan-gold opacity-50" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-pragyavan-gold/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-sage-green/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <Link to="/" className="flex items-center space-x-3 mb-6 group">
                <img
                  src="/images/logo.png"
                  alt="Pragyavan Wellness Centre"
                  className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-110"
                />
                <div>
                  <h3 className="font-display font-semibold text-xl text-pragyavan-gold">
                    Pragyavan
                  </h3>
                  <p className="text-sm text-pragyavan-cream/80">Wellness Centre</p>
                </div>
              </Link>
              <p className="text-sm text-pragyavan-cream/80 mb-6 leading-relaxed">
                {COMPANY_INFO.description}
              </p>
              <div className="flex items-center gap-2 text-xs text-pragyavan-cream/60 bg-white/5 p-3 rounded-lg backdrop-blur-sm border border-white/5">
                <Heart className="w-4 h-4 text-pragyavan-gold animate-pulse" />
                <span>Meditation, movement, and mindful living.</span>
              </div>
            </motion.div>
          </div>

          {/* Services Links */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="font-display font-semibold text-lg text-pragyavan-gold mb-6">Services</h4>
              <ul className="space-y-3">
                {footerNavigation.services.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-sm text-pragyavan-cream/80 hover:text-pragyavan-gold transition-all duration-200 hover:translate-x-1 inline-block"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Company Links */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="font-display font-semibold text-lg text-pragyavan-gold mb-6">Center</h4>
              <ul className="space-y-3">
                {footerNavigation.company.map((item) => (
                  <li key={item.path}>
                    {item.disabled ? (
                      <span className="text-sm text-pragyavan-cream/50 cursor-not-allowed">
                        {item.label}
                      </span>
                    ) : (
                      <Link
                        to={item.path}
                        className="text-sm text-pragyavan-cream/80 hover:text-pragyavan-gold transition-all duration-200 hover:translate-x-1 inline-block"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact & Support */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="font-display font-semibold text-lg text-pragyavan-gold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3 group">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-pragyavan-gold/20 transition-colors">
                    <Phone className="w-4 h-4 text-pragyavan-gold" />
                  </div>
                  <div>
                    <a
                      href={`tel:${COMPANY_INFO.contact.primary}`}
                      className="text-sm text-pragyavan-cream/80 hover:text-pragyavan-gold transition-colors duration-200 block"
                    >
                      {COMPANY_INFO.contact.primary}
                    </a>
                    <p className="text-xs text-pragyavan-cream/50">Primary</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-pragyavan-gold/20 transition-colors">
                    <Mail className="w-4 h-4 text-pragyavan-gold" />
                  </div>
                  <div>
                    <a
                      href={`mailto:${COMPANY_INFO.contact.email.primary}`}
                      className="text-sm text-pragyavan-cream/80 hover:text-pragyavan-gold transition-colors duration-200 block"
                    >
                      {COMPANY_INFO.contact.email.primary}
                    </a>
                    <p className="text-xs text-pragyavan-cream/50">General Inquiries</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-pragyavan-gold/20 transition-colors">
                    <MapPin className="w-4 h-4 text-pragyavan-gold" />
                  </div>
                  <div className="text-sm text-pragyavan-cream/80">
                    <p>Near Mangalore Airport</p>
                    <p>Mangalore, Karnataka 574142</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-pragyavan-cream/10 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex space-x-4"
            >
              {socialLinks.map((social) => {
                const IconComponent = iconMap[social.icon];
                return (
                  <motion.a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center text-pragyavan-cream/60 hover:text-pragyavan-gold hover:bg-white/10 transition-all duration-300 backdrop-blur-sm border border-white/5"
                  >
                    <IconComponent className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </motion.div>

            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center md:text-right"
            >
              <p className="text-sm text-pragyavan-cream/60">
                © {currentYear} {COMPANY_INFO.name}. All rights reserved.
              </p>
              <div className="flex items-center justify-center md:justify-end gap-4 mt-2">
                {footerNavigation.support.map((item) => (
                  <a
                    key={item.path}
                    href={item.path}
                    className="text-xs text-pragyavan-cream/40 hover:text-pragyavan-gold transition-colors duration-200"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;